import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { Section } from '../common/Section'
import { Eyebrow } from '../common/Eyebrow'
import { SplitHeading } from '../animation/SplitHeading'
import { Reveal } from '../animation/Reveal'
import { Icon } from '../common/Icon'
import { company } from '../../data/company'
import { cn } from '../../lib/cn'

const AUDIENCES = [
  { icon: 'Sprout', title: 'Farmers', body: 'Browse the range, get crop advice from AI Mitra and raise a complaint.' },
  { icon: 'Building2', title: 'Retailers', body: 'Order stock, track every delivery and see your credit and invoices.' },
  { icon: 'MapPinned', title: 'Field team', body: 'Punch in, plan visits and place orders for retailers on the go.' },
]

const LANGUAGES = ['English', 'हिंदी', 'मराठी', 'বাংলা', 'ଓଡ଼ିଆ']

export function AppDownload() {
  return (
    <Section id="get-the-app" className="section bg-canvas" ariaLabel="Download the GreenbayBio app">
      <div
        className="grain relative overflow-hidden rounded-[2rem] text-white sm:rounded-[2.5rem]"
        style={{
          background:
            'radial-gradient(55% 65% at 80% 35%, rgba(69,168,43,0.38) 0%, transparent 70%),' +
            'radial-gradient(45% 55% at 0% 100%, rgba(21,154,60,0.22) 0%, transparent 70%),' +
            '#06231a',
        }}
      >
        <div className="relative grid lg:grid-cols-[1.05fr_0.95fr]">
          {/* Copy */}
          <div className="px-6 pb-4 pt-12 sm:px-12 sm:pt-14 lg:py-20 lg:pl-16 lg:pr-4">
            <Eyebrow tone="light">Get the app</Eyebrow>
            <SplitHeading
              as="h2"
              text="GreenbayBio, right in your pocket."
              className="mt-4 font-display text-h1 font-semibold text-white"
            />
            <p className="mt-5 max-w-md text-lead text-white/70">
              One app for everyone who grows with us — each person sees the tools made for their work.
            </p>

            <Reveal className="mt-8 max-w-md divide-y divide-white/10 border-y border-white/10" stagger={0.08} y={16}>
              {AUDIENCES.map((a) => (
                <div key={a.title} className="flex items-start gap-4 py-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/[0.07] text-brand-300 ring-1 ring-white/10">
                    <Icon name={a.icon} className="h-[18px] w-[18px]" />
                  </span>
                  <p className="text-[0.95rem] leading-relaxed text-white/60">
                    <span className="font-semibold text-white">{a.title}. </span>
                    {a.body}
                  </p>
                </div>
              ))}
            </Reveal>

            <Reveal className="mt-9 flex items-center gap-5" y={14}>
              {company.apps.playStore && <QrTile url={company.apps.playStore} />}
              <div className="flex flex-wrap gap-3 lg:flex-col">
                <StoreBadge store="play" href={company.apps.playStore} />
                <StoreBadge store="apple" href={company.apps.appStore} />
              </div>
            </Reveal>

            <p className="mt-7 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-white/45">
              <span className="font-medium text-white/65">Free</span>
              <span aria-hidden="true">·</span>
              <span>Available in</span>
              {LANGUAGES.map((l, i) => (
                <span key={l} className="text-white/65">
                  {l}
                  {i < LANGUAGES.length - 1 && <span className="ml-2.5 text-white/25" aria-hidden="true">/</span>}
                </span>
              ))}
            </p>
          </div>

          {/* Devices — the lead phone rises out of the panel's bottom edge */}
          <DeviceStage />
        </div>
      </div>
    </Section>
  )
}

function DeviceStage() {
  return (
    <div className="relative h-[420px] sm:h-[520px] lg:h-auto lg:min-h-[640px]" aria-hidden="true">
      {/* concentric rings behind the phone */}
      {[260, 400, 540].map((d) => (
        <span
          key={d}
          className="absolute left-[58%] top-[55%] lg:top-[48%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/[0.06]"
          style={{ width: d, height: d }}
        />
      ))}

      <Phone
        src="/images/app/retailer-order-tracking.webp"
        className="absolute bottom-[-30px] left-1/2 w-[160px] -translate-x-[98%] -rotate-[8deg] opacity-90 sm:w-[200px] lg:bottom-[-10px] lg:w-[240px]"
      />
      <Phone
        src="/images/app/farmer-catalog.webp"
        lead
        className="absolute bottom-[-90px] left-1/2 z-10 w-[205px] -translate-x-[30%] sm:w-[250px] lg:bottom-[-50px] lg:w-[310px]"
      />

      {/* Floating callouts, drawn from real app screens */}
      <div className="absolute bottom-[16%] left-[3%] z-20 hidden motion-safe:animate-float-y sm:block lg:left-[2%]">
        <Callout icon="CheckCircle2" title="Order delivered" body="Tracked from order to doorstep" />
      </div>
      <div className="absolute bottom-[58%] right-[3%] z-20 lg:bottom-[66%] hidden motion-safe:animate-float-y sm:block [animation-delay:-3s]">
        <Callout icon="Sparkles" title="Ask AI Mitra" body="Crop advice in your language" />
      </div>
    </div>
  )
}

function Callout({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/60 bg-white/95 py-3 pl-3 pr-4 text-ink shadow-[0_24px_48px_-20px_rgba(0,0,0,0.55)] backdrop-blur">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
        <Icon name={icon} className="h-[18px] w-[18px]" />
      </span>
      <span className="leading-tight">
        <span className="block text-sm font-semibold">{title}</span>
        <span className="mt-0.5 block text-xs text-ink-500">{body}</span>
      </span>
    </div>
  )
}

/** Desktop-only: people browsing on a laptop scan this with their phone instead. */
function QrTile({ url }: { url: string }) {
  const [src, setSrc] = useState('')
  useEffect(() => {
    let live = true
    // Vector output; margin 2 keeps the quiet zone. 41 modules x 3px = 123px content (131 - 2x4 padding), so
    // every module lands on whole pixels even on 1x screens.
    QRCode.toString(url, { type: 'svg', margin: 2, errorCorrectionLevel: 'M', color: { dark: '#06231a', light: '#ffffff' } })
      .then((svg) => live && setSrc(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [url])
  if (!src) return null
  return (
    <figure className="hidden shrink-0 flex-col items-center gap-2 lg:flex">
      <img src={src} alt="QR code for the GreenbayBio app on Google Play" className="h-[131px] w-[131px] rounded-xl bg-white p-1" />
      <figcaption className="text-[0.7rem] text-white/50">Scan to install</figcaption>
    </figure>
  )
}

function StoreBadge({ store, href }: { store: 'play' | 'apple'; href: string }) {
  const isPlay = store === 'play'
  const kicker = isPlay ? 'GET IT ON' : 'Download on the'
  const label = isPlay ? 'Google Play' : 'App Store'
  const body = (
    <>
      {isPlay ? <PlayLogo /> : <AppleLogo />}
      <span className="flex flex-col text-left leading-none">
        <span className={cn('text-[0.6rem] tracking-wide text-white/75', isPlay && 'uppercase')}>
          {href ? kicker : 'Coming soon on the'}
        </span>
        <span className="mt-1 text-[1.15rem] font-semibold tracking-tight">{label}</span>
      </span>
    </>
  )
  const base = 'inline-flex h-[52px] min-w-[164px] items-center gap-3 rounded-xl bg-black px-4 text-white ring-1 ring-white/20'

  if (!href) {
    return (
      <span className={cn(base, 'cursor-default opacity-40')} aria-disabled="true" title="iPhone app coming soon">
        {body}
      </span>
    )
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${kicker} ${label}`}
      className={cn(base, 'transition duration-300 hover:-translate-y-0.5 hover:ring-white/40')}
    >
      {body}
    </a>
  )
}

function PlayLogo() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7 shrink-0" aria-hidden="true">
      <path d="M3.6 1.8 13.3 12l-9.7 10.2c-.4-.2-.6-.6-.6-1.1V2.9c0-.5.2-.9.6-1.1Z" fill="#4285F4" />
      <path d="M3.6 1.8 13.3 12l3.1-3.2L5 2.1c-.5-.3-1-.4-1.4-.3Z" fill="#34A853" />
      <path d="m3.6 22.2 9.7-10.2 3.1 3.2L5 21.9c-.5.3-1 .4-1.4.3Z" fill="#EA4335" />
      <path d="m16.4 8.8 3.9 2.3c.7.4.7 1.4 0 1.8l-3.9 2.3-3.1-3.2 3.1-3.2Z" fill="#FBBC04" />
    </svg>
  )
}

function AppleLogo() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7 shrink-0" fill="currentColor" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  )
}

/**
 * A modern Android handset drawn in CSS: brushed-metal rim, thin black bezel, punch-hole camera
 * (the screenshots' status bar leaves its centre clear), side keys, and a faint glass sheen.
 */
function Phone({ src, className, lead }: { src: string; className?: string; lead?: boolean }) {
  return (
    <div className={className}>
      <div className="relative">
        <span className="absolute -right-[2px] top-[20%] h-[11%] w-[3px] rounded-r-sm bg-gradient-to-b from-[#3b403d] via-[#6b716d] to-[#2b2f2d]" />
        <span className="absolute -right-[2px] top-[34%] h-[6%] w-[3px] rounded-r-sm bg-gradient-to-b from-[#3b403d] via-[#6b716d] to-[#2b2f2d]" />
        <div
          className={cn(
            'relative rounded-[2rem] p-[3px] sm:rounded-[2.6rem]',
            lead
              ? 'shadow-[0_70px_100px_-30px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.06)]'
              : 'shadow-[0_40px_70px_-30px_rgba(0,0,0,0.6)]',
          )}
          style={{ background: 'linear-gradient(145deg,#6a706c 0%,#1b1e1c 22%,#454a47 50%,#141614 78%,#575c59 100%)' }}
        >
          <div className="rounded-[1.85rem] bg-black p-[5px] sm:rounded-[2.4rem] sm:p-[6px]">
            <div className="relative overflow-hidden rounded-[1.6rem] bg-black sm:rounded-[2.05rem]">
              <img src={src} alt="" width={540} height={1212} loading="lazy" decoding="async" className="block h-auto w-full" />
              <span
                className="absolute left-1/2 top-[1.35%] h-[2.4%] -translate-x-1/2 rounded-full bg-black ring-1 ring-white/10"
                style={{ aspectRatio: '1 / 1' }}
              />
              <span
                className="pointer-events-none absolute inset-0"
                style={{ background: 'linear-gradient(115deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.04) 28%, transparent 42%)' }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
